import Layout from "../Layout";

const TABLE_OF_CONTENTS = [
  { id: "who-we-are", label: "Who we are" },
  { id: "our-services", label: "Our Services" },
  { id: "eligibility-and-accounts", label: "Eligibility and accounts" },
  { id: "plans-fees-and-billing", label: "Plans, fees, and billing" },
  { id: "acceptable-use", label: "Acceptable use" },
  { id: "your-responsibilities", label: "Your responsibilities" },
  { id: "intellectual-property", label: "Intellectual property" },
  { id: "third-party-services", label: "Third-party services" },
  { id: "data-privacy-and-retention", label: "Data privacy and retention" },
  { id: "service-availability-and-changes", label: "Service availability and changes" },
  { id: "disclaimers-and-limitation-of-liability", label: "Disclaimers and limitation of liability" },
  { id: "term-and-termination", label: "Term and termination" },
  { id: "changes-to-these-terms", label: "Changes to these Terms" },
  { id: "governing-law", label: "Governing law" },
  { id: "contact-us", label: "Contact us" },
] as const;

function Hero(): React.ReactElement {
  return (
    <section className="px-4 pt-40 pb-10">
      <div className="max-w-3xl mx-auto text-center">
        <h1 className="mb-4 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
          Terms of Service
        </h1>
        <p className="text-surface-foreground/60">
          Last updated: 28 November 2025
        </p>
      </div>
    </section>
  );
}

function TableOfContents(): React.ReactElement {
  return (
    <nav aria-label="Table of contents" className="px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs font-medium text-surface-foreground/30 uppercase tracking-wider mb-3">
          Contents
        </p>
        <ol className="space-y-1 text-[15px]">
          {TABLE_OF_CONTENTS.map(({ id, label }, index) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="text-surface-foreground/80 hover:underline"
              >
                <span className="text-surface-foreground/50 tabular-nums mr-2">
                  {index + 1}.
                </span>
                {label}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function Intro(): React.ReactElement {
  return (
    <section className="px-4 pt-4 pb-8">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <p>
          These Terms of Service ("Terms") govern your access to and use of Infralyst's website and
          products (together, the "Services").
        </p>
        <p>
          By creating an account, starting a workspace, or using the Services in any way, you agree
          to these Terms. If you are using the Services on behalf of a company or other entity, you
          represent that you are authorized to accept these Terms for that entity.
        </p>
        <p>If you do not agree to these Terms, do not use the Services.</p>
      </div>
    </section>
  );
}

function WhoWeAre(): React.ReactElement {
  return (
    <section id="who-we-are" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">1.</span>
          Who we are
        </h2>
        <p>Infralyst is operated by:</p>
        <address className="not-italic mb-4 text-[15px] leading-7 pl-4 border-l-2 border-surface-foreground/10">
          <strong>Saankofi Limited</strong>
          <br />
          A company incorporated in Hong Kong
          <br />
          Company number: 79143950
          <br />
          Registered address: 14/F Hill Lodge, 1 Lok Fung Path, Fo Tan,
          <br />
          New Territories, Hong Kong
        </address>
        <p>
          In these Terms, "Infralyst", "we", "us" and "our" refer to Saankofi Limited. "You" and
          "your" refer to the individual user or the organization on whose behalf the Services are
          used.
        </p>
      </div>
    </section>
  );
}

function OurServices(): React.ReactElement {
  return (
    <section id="our-services" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">2.</span>
          Our Services
        </h2>
        <p>
          Infralyst analyzes your cloud infrastructure (currently focused on AWS) and related
          telemetry to identify potential cost-saving changes, and can generate Terraform pull
          requests in your GitHub repositories to implement those changes.
        </p>
        <p>Key characteristics of the Service:</p>
        <ul>
          <li>
            Access to your AWS accounts is via a read-only IAM role with the minimum privileges
            needed to perform analysis and open PRs.
          </li>
          <li>
            Git changes are delivered as branches and pull requests in your repositories. Infralyst
            never merges changes; your team reviews, tests, and merges (or closes) any PRs.
          </li>
          <li>
            Slack integration, if enabled, is optional and used to send notifications and trigger PR
            generation from your channels.
          </li>
        </ul>
        <p>
          We may update, improve, or change the Services from time to time. If we make changes that
          materially affect your use of the Services, we'll take reasonable steps to notify you (for
          example, via email or in-app notices).
        </p>
      </div>
    </section>
  );
}

function EligibilityAndAccounts(): React.ReactElement {
  return (
    <section id="eligibility-and-accounts" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-3 prose-h3:mt-8 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">3.</span>
          Eligibility and accounts
        </h2>

        <h3>3.1 Eligibility</h3>
        <p>You may use the Services only if:</p>
        <ul>
          <li>You are at least 18 years old, and</li>
          <li>You have the legal capacity to enter into a binding contract, and</li>
          <li>You are not barred from using the Services under applicable law.</li>
        </ul>

        <h3>3.2 Account registration</h3>
        <p>
          To use the Services you must create an account and set up at least one workspace. You
          agree to:
        </p>
        <ul>
          <li>Provide accurate and complete information (such as your name and email address).</li>
          <li>Keep your account information up to date.</li>
          <li>Maintain the security of your login credentials and not share them with others.</li>
        </ul>
        <p>You are responsible for all activity that occurs under your account.</p>

        <h3>3.3 Use on behalf of an organization</h3>
        <p>
          If you create or join a workspace on behalf of a company or other organization, that
          organization is considered the customer and is responsible for:
        </p>
        <ul>
          <li>Controlling access to the workspace and its data.</li>
          <li>Managing invitations, roles, and membership.</li>
          <li>Ensuring that all users comply with these Terms.</li>
        </ul>
      </div>
    </section>
  );
}

function PlansFeesAndBilling(): React.ReactElement {
  return (
    <section id="plans-fees-and-billing" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-3 prose-h3:mt-8 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">4.</span>
          Plans, fees, and billing
        </h2>

        <h3>4.1 Free plan</h3>
        <p>
          Infralyst offers a Free plan with a limited number of lifetime PRs per
          workspace and access to core analysis features. We may change the limits of the Free plan
          from time to time.
        </p>

        <h3>4.2 Paid plans</h3>
        <p>
          We may offer one or more paid subscription plans (such as Infralyst Pro) with
          additional features or higher limits.
        </p>
        <ul>
          <li>
            Subscription fees are typically charged per workspace, on a recurring basis (for
            example, monthly).
          </li>
          <li>
            Prices, features, and limits for each plan are described on our Pricing page at the time
            you subscribe.
          </li>
        </ul>

        <h3>4.3 Billing</h3>
        <p>
          Subscriptions are billed through our payment provider (currently Stripe). By starting a
          paid plan, you authorize us and our payment provider to charge the applicable fees using
          your selected payment method.
        </p>
        <p>Unless otherwise stated:</p>
        <ul>
          <li>Fees are charged in advance for the applicable billing period.</li>
          <li>
            All fees are non-refundable, except where required by law or where we explicitly state
            otherwise.
          </li>
        </ul>

        <h3>4.4 Changes to plans and pricing</h3>
        <p>
          We may change our fees, plans, or limits from time to time. If we make changes that apply
          to an existing subscription:
        </p>
        <ul>
          <li>We will notify you in advance, and</li>
          <li>
            The changes will take effect from the start of your next billing period (unless
            otherwise stated).
          </li>
        </ul>
        <p>
          If you do not agree to the new prices or plan structure, you may cancel your subscription
          before the change takes effect.
        </p>

        <h3>4.5 Cancellation</h3>
        <p>
          You can cancel a paid subscription at any time in the app or by contacting us.
          Cancellation:
        </p>
        <ul>
          <li>Stops future billing at the end of the current billing period, and</li>
          <li>
            Does not automatically delete your account or workspace data (see Section 9, "Data
            retention").
          </li>
        </ul>
        <p>Downgrading to the Free plan may result in reduced features or limits.</p>
      </div>
    </section>
  );
}

function AcceptableUse(): React.ReactElement {
  return (
    <section id="acceptable-use" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ol:my-4 prose-ol:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">5.</span>
          Acceptable use
        </h2>
        <p>You agree not to:</p>
        <ol>
          <li>Use the Services in any way that violates applicable law or regulations.</li>
          <li>Interfere with or disrupt the integrity or performance of the Services.</li>
          <li>
            Attempt to gain unauthorized access to the Services, other accounts, or our
            infrastructure.
          </li>
          <li>
            Reverse engineer, decompile, or attempt to derive source code from any non-open-source
            component of the Services, except to the extent allowed by law.
          </li>
          <li>
            Use the Services to build or train a competing product using our proprietary models,
            heuristics, or user interface.
          </li>
          <li>Misrepresent your identity or affiliation when using the Services.</li>
        </ol>
        <p>
          We reserve the right to suspend or terminate access to the Services if we reasonably
          believe you have violated these Terms or are using the Services in a way that could cause
          harm to us, other users, or third parties.
        </p>
      </div>
    </section>
  );
}

function YourResponsibilities(): React.ReactElement {
  return (
    <section id="your-responsibilities" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">6.</span>
          Your responsibilities
        </h2>
        <p>You are responsible for:</p>
        <ul>
          <li>
            <strong>Your infrastructure and code.</strong> Infralyst surfaces recommendations and
            opens PRs, but you and your team decide whether to apply any changes. You must review,
            test, and validate changes in accordance with your own processes.
          </li>
          <li>
            <strong>Configuration of access.</strong> You are responsible for correctly configuring
            AWS IAM roles, GitHub permissions, and any other integrations, including revoking access
            when no longer needed.
          </li>
          <li>
            <strong>Backups and recovery.</strong> You should maintain your own backups and recovery
            procedures for your Terraform states, repositories, and infrastructure.
          </li>
          <li>
            <strong>Compliance with third-party terms.</strong> Your use of AWS, GitHub, Slack,
            Stripe, and any other third-party services remains subject to their own terms and
            policies.
          </li>
        </ul>
        <p>
          Infralyst is not responsible for configuration errors, misapplied changes, or
          infrastructure issues caused by your use (or non-use) of the Services.
        </p>
      </div>
    </section>
  );
}

function IntellectualProperty(): React.ReactElement {
  return (
    <section id="intellectual-property" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-3 prose-h3:mt-8 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">7.</span>
          Intellectual property
        </h2>

        <h3>7.1 Our rights</h3>
        <p>
          The Services, including the Infralyst name, logo, product design, documentation, and any
          proprietary models, heuristics, or software, are owned by Saankofi Limited or our
          licensors and are protected by copyright, trademark, and other laws.
        </p>
        <p>
          Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable
          license to use the Services for your internal business purposes.
        </p>

        <h3>7.2 Your content</h3>
        <p>
          You retain all rights to your own content, including your Terraform configurations, code
          repositories, infrastructure metadata, and any other materials you provide or connect to
          the Services ("Customer Data").
        </p>
        <p>You grant us a limited license to process Customer Data only as necessary to:</p>
        <ul>
          <li>Provide and improve the Services.</li>
          <li>Generate analyses, recommendations, and pull requests.</li>
          <li>Maintain security and monitor performance.</li>
        </ul>
        <p>
          We do not claim ownership of Customer Data, and we will not use it for advertising.
        </p>

        <h3>7.3 Feedback</h3>
        <p>
          If you provide suggestions or feedback about the Services, you agree that we may use that
          feedback without restriction or obligation to you.
        </p>
      </div>
    </section>
  );
}

function ThirdPartyServices(): React.ReactElement {
  return (
    <section id="third-party-services" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">8.</span>
          Third-party services
        </h2>
        <p>
          The Services may integrate with or rely on third-party products and services (such as AWS,
          GitHub, Slack, Stripe, Sentry, PostHog, and Fireworks AI).
        </p>
        <ul>
          <li>
            We do not control third-party services and are not responsible for their actions,
            content, or availability.
          </li>
          <li>
            Your use of third-party services is governed by their own terms and privacy policies.
          </li>
        </ul>
        <p>
          Where we use third-party providers to process data on our behalf, we do so in accordance
          with our Privacy Policy.
        </p>
      </div>
    </section>
  );
}

function DataPrivacyAndRetention(): React.ReactElement {
  return (
    <section id="data-privacy-and-retention" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">9.</span>
          Data privacy and retention
        </h2>
        <p>
          Your use of the Services is also governed by our{" "}
          <a href="/privacy-policy">Privacy Policy</a>, which explains what data we collect, how we
          use it, and how we protect it.
        </p>
        <p>In summary:</p>
        <ul>
          <li>
            We focus on infrastructure metadata, usage patterns, and account information necessary
            to provide the Services.
          </li>
          <li>We do not store AWS access keys; access is via role assumption.</li>
          <li>
            You can delete your workspace and associated data from within the app, subject to any
            minimum retention periods required for legal, accounting, or security reasons.
          </li>
        </ul>
        <p>Please read the full Privacy Policy for details.</p>
      </div>
    </section>
  );
}

function ServiceAvailabilityAndChanges(): React.ReactElement {
  return (
    <section id="service-availability-and-changes" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">10.</span>
          Service availability and changes
        </h2>
        <p>We aim to provide a reliable and secure service, but:</p>
        <ul>
          <li>The Services are provided on an "as is" and "as available" basis.</li>
          <li>
            We may temporarily suspend or limit the Services for maintenance, security reasons, or
            operational changes.
          </li>
          <li>
            We may modify or discontinue parts of the Services, but we will try to avoid changes
            that materially reduce core functionality for existing paid users without prior notice.
          </li>
        </ul>
        <p>
          We do not guarantee that the Services will be error-free, uninterrupted, or that they will
          identify every possible cost-saving opportunity.
        </p>
      </div>
    </section>
  );
}

function DisclaimersAndLimitationOfLiability(): React.ReactElement {
  return (
    <section id="disclaimers-and-limitation-of-liability" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-3 prose-h3:mt-8 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">11.</span>
          Disclaimers and limitation of liability
        </h2>

        <h3>11.1 No guarantee of savings or outcomes</h3>
        <p>Infralyst provides recommendations and tools to help you manage cloud costs, but:</p>
        <ul>
          <li>We do not guarantee any specific level of savings or performance improvements.</li>
          <li>
            Recommendations are based on usage patterns, heuristics, and models that may not capture
            every nuance of your workloads.
          </li>
        </ul>
        <p>
          You remain responsible for evaluating the suitability of any recommendation for your
          environment.
        </p>

        <h3>11.2 Disclaimer of warranties</h3>
        <p>
          To the fullest extent permitted by law, we disclaim all warranties, whether express,
          implied, or statutory, including any implied warranties of merchantability, fitness for a
          particular purpose, and non-infringement.
        </p>

        <h3>11.3 Limitation of liability</h3>
        <p>To the fullest extent permitted by law:</p>
        <ul>
          <li>
            Our total liability arising out of or relating to the Services or these Terms is limited
            to the greater of (a) the amount you paid us in the 12 months preceding the event giving
            rise to the claim, or (b) USD 100.
          </li>
          <li>
            We are not liable for any indirect, incidental, consequential, special, or punitive
            damages, or for any loss of profits, revenue, data, or goodwill.
          </li>
        </ul>
        <p>
          These limitations apply whether or not we have been informed of the possibility of such
          damages.
        </p>
        <p>
          Some jurisdictions do not allow the limitation of liability for certain types of damages;
          in those cases, our liability will be limited to the maximum extent permitted by law.
        </p>
      </div>
    </section>
  );
}

function TermAndTermination(): React.ReactElement {
  return (
    <section id="term-and-termination" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">12.</span>
          Term and termination
        </h2>
        <p>These Terms apply from the moment you first use the Services and continue until:</p>
        <ul>
          <li>You delete your account and all associated workspaces, or</li>
          <li>We terminate or suspend your access in accordance with these Terms.</li>
        </ul>
        <p>We may suspend or terminate your access to the Services if:</p>
        <ul>
          <li>You materially breach these Terms, or</li>
          <li>We are required to do so by law, or</li>
          <li>
            Continuing to provide the Services to you is no longer commercially or technically
            feasible.
          </li>
        </ul>
        <p>Upon termination:</p>
        <ul>
          <li>Your right to access the Services ends.</li>
          <li>
            We may delete or anonymize your data in accordance with our data retention practices and
            applicable law.
          </li>
        </ul>
        <p>
          Sections that by their nature should survive termination (including payment obligations,
          IP provisions, disclaimers, and limitations of liability) will continue to apply.
        </p>
      </div>
    </section>
  );
}

function ChangesToTheseTerms(): React.ReactElement {
  return (
    <section id="changes-to-these-terms" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">13.</span>
          Changes to these Terms
        </h2>
        <p>
          We may update these Terms from time to time. If we make material changes, we will provide
          notice (for example by email or in-app).
        </p>
        <p>
          When changes take effect, your continued use of the Services will constitute acceptance of
          the updated Terms. If you do not agree to the new Terms, you must stop using the Services
          and, if applicable, cancel any paid subscriptions.
        </p>
      </div>
    </section>
  );
}

function GoverningLaw(): React.ReactElement {
  return (
    <section id="governing-law" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">14.</span>
          Governing law
        </h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of Hong Kong.
        </p>
        <p>
          Any disputes arising out of or relating to these Terms or the Services will be subject to
          the exclusive jurisdiction of the courts of Hong Kong, without regard to conflict-of-law
          rules.
        </p>
      </div>
    </section>
  );
}

function ContactUs(): React.ReactElement {
  return (
    <section id="contact-us" className="px-4 pt-10 pb-16 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">15.</span>
          Contact us
        </h2>
        <p>If you have questions about these Terms, please contact us at:</p>
        <ul>
          <li>
            <strong>Email:</strong> <a href="mailto:hello@infralyst.io">hello@infralyst.io</a>
          </li>
          <li>
            <strong>Postal address:</strong> Saankofi Limited, 14/F Hill Lodge, 1 Lok Fung Path, Fo
            Tan, New Territories, Hong Kong
          </li>
        </ul>
      </div>
    </section>
  );
}

export default function Show(): React.ReactElement {
  return (
    <Layout
      title="Terms of Service"
      description="Review Infralyst's terms of service for using our cloud cost optimization and Terraform PR generation services."
      showGradient={false}
    >
      <Hero />
      <TableOfContents />
      <Intro />
      <WhoWeAre />
      <OurServices />
      <EligibilityAndAccounts />
      <PlansFeesAndBilling />
      <AcceptableUse />
      <YourResponsibilities />
      <IntellectualProperty />
      <ThirdPartyServices />
      <DataPrivacyAndRetention />
      <ServiceAvailabilityAndChanges />
      <DisclaimersAndLimitationOfLiability />
      <TermAndTermination />
      <ChangesToTheseTerms />
      <GoverningLaw />
      <ContactUs />
    </Layout>
  );
}
